/**
 * Which rejection email a candidate gets, by department and stage.
 *
 * SOURCE OF TRUTH is the template code in proxy-server/lib/rejectionEmail.js. This module
 * exists so the modal can *explain* the rule to a recruiter; it does not decide anything.
 *
 * proxy-server/lib/rejectionEmail.rules.test.js drives the real builder across every
 * department × stage and asserts it agrees with the table below — so if the templates
 * change and this is not updated, that test fails.
 */

export const DEPARTMENTS = ['Tech', 'Growth', 'Finance & Legal', 'Operations', 'People', 'Fleet'] as const;
export const STAGES = ['first', 'case', 'final'] as const;

export type Stage = (typeof STAGES)[number];
export type ContentKind = 'on-request' | 'written' | 'booking';

export const STAGE_LABEL: Record<Stage, string> = {
  first: 'First interview',
  case: 'Case / technical',
  final: 'Final',
};

export const CONTENT_LABEL: Record<ContentKind, string> = {
  'on-request': 'Feedback on request',
  written: 'Written feedback in the email',
  booking: '15-min feedback call link',
};

export const CONTENT_DESCRIPTION: Record<ContentKind, string> = {
  'on-request': 'The email invites the candidate to ask for feedback by email. Nothing is written out.',
  written: 'The AGG-checked feedback is written into the email body itself.',
  booking: "The email links the TA manager's booking page for a 15-minute feedback call.",
};

/**
 * Mirrors the template selection in rejectionEmail.js: first stage is always
 * feedback-on-request; a case/technical round writes the feedback out for Tech only and
 * offers a call to everyone else; a final round always offers a call.
 */
export function contentFor(department: string, stage: string): ContentKind {
  const isTech = department.toLowerCase().includes('tech');
  if (stage === 'case') return isTech ? 'written' : 'booking';
  if (stage === 'final') return 'booking';
  // Unknown stages fall back to the first-interview template, as the builder does.
  return 'on-request';
}
